import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { useCrud } from '../../hooks/useApi';
import { complaintService } from '../../services/api';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import CategoryModal from '../../components/Complaint/CategoryModal';
import CategoryTable from '../../components/Complaint/CategoryTable';
import CategoryFilters from '../../components/Complaint/CategoryFilters';
import { useTranslation } from 'react-i18next';

const Categories = () => {
  const { t } = useTranslation('complaints');

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [filters, setFilters] = useState({ search: '', sortBy: 'name', sortOrder: 'asc' });

  const { useList, useCreate, useUpdate, useDelete } = useCrud('categories', complaintService.categoryCrud);

  const { data: categoriesData, isLoading, error } = useList();
  const categoriesList = Array.isArray(categoriesData) ? categoriesData : categoriesData?.data ?? [];

  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const handleCreate = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = async (data) => {
    try {
      if (editingCategory) {
        updateMutation.mutate({ id: editingCategory.id, data });
      } else {
        createMutation.mutate(data);
      }
      setShowModal(false);
      setEditingCategory(null);
    } catch (err) {
      console.error('Erro ao salvar categoria:', err);
    }
  };

  const filteredCategories = categoriesList.filter((cat) => {
    const matchesSearch = !filters.search || (cat.name && cat.name.toLowerCase().includes(filters.search.toLowerCase()));
    return matchesSearch;
  });

  const sortedCategories = [...filteredCategories].sort((a, b) => {
    const aVal = a[filters.sortBy] || a.name || '';
    const bVal = b[filters.sortBy] || b.name || '';
    if (filters.sortOrder === 'asc') return String(aVal).localeCompare(String(bVal));
    return String(bVal).localeCompare(String(aVal));
  });

  if (error) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("categoriesTitle")}
          description="Classificação hierárquica das denúncias ambientais."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Denúncias', to: '/complaint/categories' },
            { label: 'Categorias' },
          ]}
        >
          <Can permission={P.COMPLAINT_ACCESS}>
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-primary inline-flex items-center gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Nova categoria
            </button>
          </Can>
        </PageHeader>

        <div className="data-list-card">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <CategoryFilters filters={filters} onFiltersChange={setFilters} />
          </div>
          <div className="p-5 sm:p-6">
            <CategoryTable
              categories={sortedCategories}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCreate={handleCreate}
            />
          </div>
        </div>
      </div>

      {showModal && (
        <CategoryModal
          category={editingCategory}
          categories={categoriesList}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingCategory(null);
          }}
        />
      )}
    </FormPageShell>
  );
};

export default Categories;
